'use client';

import {
  Box,
  Button,
  CloseButton,
  Dialog,
  Flex,
  Grid,
  HStack,
  Input,
  Portal,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Globe, RefreshCw, Search, Gauge, Save, Trash2 } from 'lucide-react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '@/app/auth-provider';
import { AppShellToolbarPortal } from '@/components/core/app-layout/AppShellToolbar';
import {
  WebsiteAnalyticsViews,
  WebsiteSharedControls,
  getWebsiteDateRange,
  type WebsiteAnalyticsSource,
  type WebsiteDateRange,
  type WebsiteDateRangeKey,
  type WebsiteTab,
} from '@/components/pages/WebsiteAnalyticsViews';
import { PagesInventoryTable, type PageInventoryRow } from '@/components/pages/PageInventoryTable';
import { InlineSpinner } from '@/components/shared/PageLoader';
import { InlineFilterGroup } from '@/components/shared/InlineFilterControls';
import { FilterBar, FilterClearButton, FilterPopover } from '@/components/shared/FilterBar';
import { FilterPill } from '@/components/shared/ui/FilterPill';
import { useProject } from '@/contexts/ProjectContext';
import {
  callPageReview,
  pageReviewHref,
  type PageReviewQueuePage,
  type PageReviewQueuePayload,
} from '@/lib/page-review/client';

interface ScrapedPage {
  id: string;
  crawl_id: string;
  url: string;
  normalized_url: string | null;
  domain: string | null;
  title: string | null;
  description: string | null;
  og_image: string | null;
  markdown_bytes: number | null;
  word_count: number | null;
  http_status: number | null;
  lang: string | null;
  seo_score: number | null;
  seo_grade: string | null;
  fetched_at: string | null;
  created_at: string;
  citation_count: number;
  artifact_status: string | null;
  html_sha256: string | null;
}

function metricNumber(value: unknown): number {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? parsed : 0;
}

function normalizeAnalyticsUrlKey(value: unknown): string {
  const raw = String(value || '').trim();
  if (!raw) return '';
  try {
    const withScheme = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
    const url = new URL(withScheme);
    url.hash = '';
    url.search = '';
    url.hostname = url.hostname.toLowerCase().replace(/^www\./, '');
    if (url.pathname !== '/') {
      url.pathname = url.pathname.replace(/\/+$/, '');
    }
    return `${url.hostname}${url.pathname === '/' ? '' : url.pathname}`;
  } catch {
    return raw
      .toLowerCase()
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .replace(/[?#].*$/, '')
      .replace(/\/+$/, '');
  }
}

function uxFrictionFromRow(row: Record<string, unknown>): number {
  return (
    metricNumber(row.ux_friction) ||
    metricNumber(row.dead_clicks) +
      metricNumber(row.rage_clicks) +
      metricNumber(row.excessive_scrolls) +
      metricNumber(row.quickbacks) +
      metricNumber(row.script_errors) +
      metricNumber(row.error_clicks)
  );
}

// URL Path Utilities
function getUrlPath(url: string): string {
  try {
    const value = url.trim();
    const u = value.startsWith('/')
      ? new URL(value, 'https://attune.local')
      : new URL(/^https?:\/\//i.test(value) ? value : `https://${value}`);
    return u.pathname || '/';
  } catch {
    return '/';
  }
}

function extractDomain(urlOrHost: string): string {
  try {
    const raw = (urlOrHost || '').trim();
    if (!raw) return '';
    const withScheme = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
    const u = new URL(withScheme);
    return (u.hostname || '').replace(/^www\./i, '').toLowerCase();
  } catch {
    return '';
  }
}

function getPathDepth(path: string): number {
  const clean = path.replace(/^\/|\/$/g, '');
  if (!clean) return 0;
  return clean.split('/').length;
}

interface CrawlStatus {
  crawl_id: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  brand_domain: string;
  root_url: string;
  page_count: number;
  error?: string;
  created_at: string | null;
  started_at: string | null;
  finished_at: string | null;
}

interface CrawlComparison {
  previous_crawl_id: string;
  previous_finished_at: string | null;
  added_pages: number;
  removed_pages: number;
  score_changed_pages: number;
  improved_pages: number;
  declined_pages: number;
  average_score_delta: number | null;
  top_improved_pages: CrawlScoreChange[];
  top_worsened_pages: CrawlScoreChange[];
}

interface CrawlScoreChange {
  url: string;
  title: string | null;
  previous_score: number;
  current_score: number;
  score_delta: number;
}

function crawlTimestamp(crawl: CrawlStatus): string | null {
  return crawl.finished_at || crawl.started_at || crawl.created_at;
}

function formatCrawlDate(crawl: CrawlStatus, includeTime = false): string {
  const timestamp = crawlTimestamp(crawl);
  if (!timestamp) return 'Crawl date unavailable';
  const date = new Date(timestamp);
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
    ...(includeTime ? { hour: 'numeric', minute: '2-digit' } : {}),
  }).format(date);
}

function CrawlScoreChangeList({
  label,
  rows,
  improved,
}: {
  label: string;
  rows: CrawlScoreChange[];
  improved: boolean;
}) {
  const tone = improved ? 'status.success.fg' : 'status.warning.fg';

  return (
    <Box>
      <Text fontSize="xs" fontWeight="700" color={tone} mb={1.5}>
        {improved ? '↑' : '↓'} {label}
      </Text>
      {rows.length > 0 ? (
        <VStack align="stretch" gap={2}>
          {rows.map(row => {
            const path = getUrlPath(row.url);
            return (
              <Grid key={row.url} templateColumns="minmax(0, 1fr) auto" alignItems="start" gap={2}>
                <Box minW={0} title={row.title || row.url}>
                  <Text fontSize="xs" color="ui.text.primary" fontWeight="600" truncate>
                    {row.title || path}
                  </Text>
                  {row.title ? (
                    <Text fontSize="10px" color="ui.text.tertiary" truncate>
                      {path}
                    </Text>
                  ) : null}
                </Box>
                <Box textAlign="right" flexShrink={0}>
                  <Text fontSize="xs" color={tone} fontWeight="700">
                    {row.score_delta > 0 ? '+' : ''}
                    {row.score_delta}
                  </Text>
                  <Text fontSize="10px" color="ui.text.tertiary" whiteSpace="nowrap">
                    {row.previous_score} → {row.current_score}
                  </Text>
                </Box>
              </Grid>
            );
          })}
        </VStack>
      ) : (
        <Text fontSize="xs" color="ui.text.tertiary">
          No {improved ? 'improved' : 'worsened'} pages
        </Text>
      )}
    </Box>
  );
}

interface TreeNode {
  name: string;
  path: string;
  pageCount: number;
  totalScore: number;
  avgScore: number;
  grade: string;
  issueCount?: number;
  children: TreeNode[];
}

interface PageStats {
  total: number;
  scoreDistribution: Record<string, number>;
  avgScore: number;
  pathClusters: Array<{
    name: string;
    size: number;
    avgScore: number;
    grade: string;
  }>;
  depthDistribution: Record<number, number>;
  siteTree: TreeNode | null;
}

interface PageAnalyticsMetric {
  sessions: number;
  searchClicks: number;
  searchImpressions: number;
  gscImpressions: number;
  gscClicks: number;
  gscCtr: number | null;
  gscPosition: number | null;
  bingImpressions: number;
  bingClicks: number;
  bingCtr: number | null;
  bingPosition: number | null;
  conversions: number;
  revenue: number;
  aiCitations: number;
  aiSearches: number;
  aiPrompts: number;
  aiVisibleAnswers: number;
  aiAvgRank: number | null;
  hasCitationMetric: boolean;
  uxFriction: number;
  rageClicks: number;
  deadClicks: number;
  quickbacks: number;
  errors: number;
  providers: string[];
}

interface SavedFilter {
  id: string;
  name: string;
  pathFilter: string;
  depthFilter: string;
  scoreFilter: string;
  statusFilter: string;
  searchQuery: string;
  pageIds: string[] | null;
  createdAt: string;
}

// Pagination config
const PAGE_SIZE = 100;
type AnalyticsSortColumn = 'citations' | 'impressions' | 'clicks';

function analyticsSortApiColumn(column: AnalyticsSortColumn | null) {
  if (column === 'citations') return 'citation_count';
  if (column === 'impressions') return 'search_impressions';
  if (column === 'clicks') return 'search_clicks';
  return null;
}

function websiteDateRangeFromSearchParams(
  searchParams: Pick<URLSearchParams, 'get'>
): WebsiteDateRange {
  const rangeKey = searchParams.get('range') as WebsiteDateRangeKey | null;
  const startDate = searchParams.get('start') || '';
  const endDate = searchParams.get('end') || '';
  if (
    rangeKey === 'custom' &&
    /^\d{4}-\d{2}-\d{2}$/.test(startDate) &&
    /^\d{4}-\d{2}-\d{2}$/.test(endDate) &&
    startDate <= endDate
  ) {
    return { key: 'custom', label: 'Custom', startDate, endDate };
  }
  return getWebsiteDateRange(rangeKey === '30d' || rangeKey === '90d' ? rangeKey : '7d');
}

export function PagesWorkspace({ activeWebsiteTab }: { activeWebsiteTab: WebsiteTab }) {
  const { user, userDocument } = useAuth();
  const {
    currentProject,
    projects,
    switchProject,
    loading: projectLoading,
    isReady: projectsReady,
  } = useProject();
  const searchParams = useSearchParams();
  const router = useRouter();
  const projectId = currentProject?.id || null;
  const projectScopeReady = projectsReady && !projectLoading && Boolean(projectId);

  // Request sequencing: prevents out-of-order responses from updating state
  const pagesRequestSeq = useRef(0);
  const pageReviewQueueRequestSeq = useRef(0);
  const statsRequestSeq = useRef(0);
  const crawlStatusRequestSeq = useRef(0);
  const crawlSnapshotRequestSeq = useRef(0);
  const analyticsStatusRequestSeq = useRef(0);
  const websitePerformanceRequestSeq = useRef(0);
  const projectIdRef = useRef<string | null>(projectId);
  useEffect(() => {
    projectIdRef.current = projectId;
  }, [projectId]);

  // Canonical domain filter for the currently selected project.
  // Fallbacks keep legacy accounts working (user profile brand_settings).
  const brandDomain = useMemo(() => {
    const projectDomain = (currentProject?.brand_settings?.website_domain || '').trim();
    const projectUrl = (currentProject?.brand_settings?.website_url || '').trim();
    const userDomain = (userDocument?.brand_settings?.website_domain || '').trim();
    const userUrl = (userDocument?.brand_settings?.website_url || '').trim();

    return projectDomain || extractDomain(projectUrl) || userDomain || extractDomain(userUrl) || '';
  }, [
    currentProject?.brand_settings?.website_domain,
    currentProject?.brand_settings?.website_url,
    userDocument?.brand_settings?.website_domain,
    userDocument?.brand_settings?.website_url,
  ]);

  // Initial load state - only true until first data load completes
  const [initialLoading, setInitialLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [pagesError, setPagesError] = useState<{
    projectId: string;
    message: string;
    append: boolean;
  } | null>(null);
  const [pageStats, setPageStats] = useState<PageStats | null>(null);
  const [pageStatsProjectId, setPageStatsProjectId] = useState<string | null>(null);
  const [pages, setPages] = useState<ScrapedPage[]>([]);
  const [pageReviewQueue, setPageReviewQueue] = useState<PageReviewQueuePage[]>([]);
  const [pagesProjectId, setPagesProjectId] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  // Grade distribution from server for current filter context
  const [serverGradeDistribution, setServerGradeDistribution] = useState<Record<
    string,
    number
  > | null>(null);
  const [crawlStatus, setCrawlStatus] = useState<CrawlStatus | null>(null);
  const [crawlStatusProjectId, setCrawlStatusProjectId] = useState<string | null>(null);
  const [crawlHistory, setCrawlHistory] = useState<CrawlStatus[]>([]);
  const [selectedCrawlId, setSelectedCrawlId] = useState(() => searchParams.get('crawl') || '');
  const selectedCrawlIdRef = useRef(selectedCrawlId);
  const [crawlComparison, setCrawlComparison] = useState<CrawlComparison | null>(null);
  const [crawlComparisonLoading, setCrawlComparisonLoading] = useState(false);
  const [isStartingCrawl, setIsStartingCrawl] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [websiteDateRange, setWebsiteDateRange] = useState<WebsiteDateRange>(() =>
    websiteDateRangeFromSearchParams(searchParams)
  );
  const [analyticsSources, setAnalyticsSources] = useState<WebsiteAnalyticsSource[]>([]);
  const [analyticsSourcesLoading, setAnalyticsSourcesLoading] = useState(false);
  const [pageMetricRows, setPageMetricRows] = useState<Array<Record<string, unknown>>>([]);
  const [uxMetricRows, setUxMetricRows] = useState<Array<Record<string, unknown>>>([]);

  useEffect(() => {
    const nextDateRange = websiteDateRangeFromSearchParams(searchParams);
    setWebsiteDateRange(current =>
      current.key === nextDateRange.key &&
      current.startDate === nextDateRange.startDate &&
      current.endDate === nextDateRange.endDate
        ? current
        : nextDateRange
    );
  }, [searchParams]);

  const handleWebsiteDateRangeChange = useCallback(
    (range: WebsiteDateRange) => {
      setWebsiteDateRange(range);
      const nextSearchParams = new URLSearchParams(searchParams.toString());
      nextSearchParams.set('range', range.key);
      if (range.key === 'custom') {
        nextSearchParams.set('start', range.startDate);
        nextSearchParams.set('end', range.endDate);
      } else {
        nextSearchParams.delete('start');
        nextSearchParams.delete('end');
      }
      router.replace(`?${nextSearchParams.toString()}`, { scroll: false });
    },
    [router, searchParams]
  );

  // Filters
  const pathFilter = searchParams.get('path') || '';
  const setPathFilter = useCallback(
    (path: string) => {
      const url = new URL(window.location.href);
      if (path && path !== '/') url.searchParams.set('path', path);
      else url.searchParams.delete('path');
      if (projectId) url.searchParams.set('projectId', projectId);
      window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
    },
    [projectId]
  );
  const [depthFilter, setDepthFilter] = useState('');
  const [scoreFilter, setScoreFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [analyticsSortColumn, setAnalyticsSortColumn] = useState<AnalyticsSortColumn | null>(null);
  const [analyticsSortOrder, setAnalyticsSortOrder] = useState<'asc' | 'desc'>('desc');
  const toggleAnalyticsSort = useCallback(
    (column: AnalyticsSortColumn) => {
      setAnalyticsSortOrder(order =>
        analyticsSortColumn === column ? (order === 'desc' ? 'asc' : 'desc') : 'desc'
      );
      setAnalyticsSortColumn(column);
    },
    [analyticsSortColumn]
  );

  // Saved filters
  const [savedFilters, setSavedFilters] = useState<SavedFilter[]>([]);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [newFilterName, setNewFilterName] = useState('');
  const [savingFilter, setSavingFilter] = useState(false);
  const [filterToDelete, setFilterToDelete] = useState<SavedFilter | null>(null);
  const [selectedSavedFilterId, setSelectedSavedFilterId] = useState('');

  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const skipInitialCrawlReloadRef = useRef('');

  const applyCrawlSelection = useCallback((crawlId: string) => {
    if (!crawlId || crawlId === selectedCrawlIdRef.current) return;
    selectedCrawlIdRef.current = crawlId;
    setSelectedCrawlId(crawlId);
    setCrawlComparison(null);
    crawlSnapshotRequestSeq.current += 1;
    websitePerformanceRequestSeq.current += 1;
    setPageMetricRows([]);
    setUxMetricRows([]);
    setUrlFilteredIds(null);
    setSelectedSavedFilterId('');
  }, []);

  const handleCrawlSelection = useCallback(
    (crawlId: string) => {
      if (!crawlId || crawlId === selectedCrawlIdRef.current) return;
      const nextSearchParams = new URLSearchParams(searchParams.toString());
      nextSearchParams.set('crawl', crawlId);
      nextSearchParams.delete('ids');
      router.replace(`?${nextSearchParams.toString()}`, { scroll: false });
    },
    [router, searchParams]
  );

  const crawlIdFromUrl = searchParams.get('crawl') || '';
  useEffect(() => {
    if (crawlHistory.length === 0) return;
    const requestedCrawlIsValid = crawlHistory.some(crawl => crawl.crawl_id === crawlIdFromUrl);
    const nextCrawlId = requestedCrawlIsValid ? crawlIdFromUrl : crawlHistory[0]?.crawl_id || '';
    applyCrawlSelection(nextCrawlId);

    if (crawlIdFromUrl && !requestedCrawlIsValid) {
      const nextSearchParams = new URLSearchParams(searchParams.toString());
      nextSearchParams.delete('crawl');
      router.replace(`?${nextSearchParams.toString()}`, { scroll: false });
    }
  }, [applyCrawlSelection, crawlHistory, crawlIdFromUrl, router, searchParams]);

  const projectOptions = useMemo(
    () =>
      projects.map(project => ({
        id: project.id,
        name: project.name,
        brandDomain:
          project.brand_settings?.website_domain || project.brand_settings?.website_url || null,
      })),
    [projects]
  );

  // Never render data that belongs to a different project than the currently selected one.
  // This prevents wrong-project leakage during rapid project switching or slow network responses.
  const visiblePagesError = pagesError?.projectId === projectId ? pagesError : null;
  const pagesAreForCurrentProject =
    Boolean(projectId) &&
    pagesProjectId === projectId &&
    (!visiblePagesError || visiblePagesError.append);
  const visiblePages = useMemo(
    () => (pagesAreForCurrentProject ? pages : []),
    [pagesAreForCurrentProject, pages]
  );
  const visibleTotal = pagesAreForCurrentProject ? total : 0;
  const visibleHasMore = pagesAreForCurrentProject ? hasMore : false;
  const statsAreForCurrentProject = Boolean(projectId) && pageStatsProjectId === projectId;
  const visiblePageStats = statsAreForCurrentProject ? pageStats : null;
  const crawlStatusIsForCurrentProject = Boolean(projectId) && crawlStatusProjectId === projectId;
  const visibleCrawlStatus = crawlStatusIsForCurrentProject ? crawlStatus : null;
  const selectedCrawl = useMemo(
    () => crawlHistory.find(crawl => crawl.crawl_id === selectedCrawlId) || null,
    [crawlHistory, selectedCrawlId]
  );
  const selectedCrawlDate = useMemo(
    () => (selectedCrawl ? crawlTimestamp(selectedCrawl)?.slice(0, 10) || '' : ''),
    [selectedCrawl]
  );
  const effectivePageEvidenceStartDate = selectedCrawlDate || websiteDateRange.startDate;
  const effectivePageEvidenceEndDate = selectedCrawlDate || websiteDateRange.endDate;
  const previousCrawl = useMemo(
    () =>
      crawlComparison
        ? crawlHistory.find(crawl => crawl.crawl_id === crawlComparison.previous_crawl_id) || null
        : null,
    [crawlComparison, crawlHistory]
  );
  const pageAnalyticsByUrl = useMemo(() => {
    const metrics = new Map<string, PageAnalyticsMetric>();
    const ensureForRow = (row: Record<string, unknown>) => {
      const keys = Array.from(
        new Set(
          [
            normalizeAnalyticsUrlKey(row.normalized_url),
            normalizeAnalyticsUrlKey(row.normalizedUrl),
            normalizeAnalyticsUrlKey(row.url),
          ].filter(Boolean)
        )
      );
      if (keys.length === 0) return null;

      const existing = keys.map(key => metrics.get(key)).find(Boolean);
      const target: PageAnalyticsMetric = existing || {
        sessions: 0,
        searchClicks: 0,
        searchImpressions: 0,
        gscImpressions: 0,
        gscClicks: 0,
        gscCtr: null,
        gscPosition: null,
        bingImpressions: 0,
        bingClicks: 0,
        bingCtr: null,
        bingPosition: null,
        conversions: 0,
        revenue: 0,
        aiCitations: 0,
        aiSearches: 0,
        aiPrompts: 0,
        aiVisibleAnswers: 0,
        aiAvgRank: null,
        hasCitationMetric: false,
        uxFriction: 0,
        rageClicks: 0,
        deadClicks: 0,
        quickbacks: 0,
        errors: 0,
        providers: [],
      };
      keys.forEach(key => metrics.set(key, target));
      return target;
    };

    pageMetricRows.forEach(row => {
      const target = ensureForRow(row);
      if (!target) return;
      const search = (row.search || {}) as Record<string, Record<string, unknown>>;
      const gsc = search.gsc || {};
      const bing = search.bing || {};
      const behavior = (row.behavior || {}) as Record<string, unknown>;
      const ai = (row.ai || {}) as Record<string, unknown>;
      const experience = (row.experience || {}) as Record<string, unknown>;
      const gscImpressions = metricNumber(gsc.impressions);
      const gscClicks = metricNumber(gsc.clicks);
      const bingImpressions = metricNumber(bing.impressions);
      const bingClicks = metricNumber(bing.clicks);

      target.gscImpressions += gscImpressions;
      target.gscClicks += gscClicks;
      target.gscCtr = gsc.ctr == null ? target.gscCtr : Number(gsc.ctr);
      target.gscPosition = gsc.position == null ? target.gscPosition : Number(gsc.position);
      target.bingImpressions += bingImpressions;
      target.bingClicks += bingClicks;
      target.bingCtr = bing.ctr == null ? target.bingCtr : Number(bing.ctr);
      target.bingPosition = bing.position == null ? target.bingPosition : Number(bing.position);
      target.searchClicks += gscClicks + bingClicks + metricNumber(row.search_clicks);
      target.searchImpressions +=
        gscImpressions + bingImpressions + metricNumber(row.search_impressions);
      target.sessions += metricNumber(behavior.sessions) || metricNumber(row.sessions);
      target.conversions += metricNumber(behavior.conversions) || metricNumber(row.conversions);
      target.revenue += metricNumber(behavior.revenue) || metricNumber(row.revenue);
      target.aiCitations = Math.max(
        target.aiCitations,
        metricNumber(ai.citationCount) || metricNumber(row.ai_citations)
      );
      target.aiSearches = Math.max(target.aiSearches, metricNumber(ai.relatedFanoutQueryCount));
      target.aiPrompts = Math.max(target.aiPrompts, metricNumber(ai.relatedPromptCount));
      target.aiVisibleAnswers = Math.max(
        target.aiVisibleAnswers,
        metricNumber(ai.visibleAnswerCount)
      );
      target.aiAvgRank = ai.avgRank == null ? target.aiAvgRank : Number(ai.avgRank);
      target.uxFriction += metricNumber(experience.uxIssues);
      target.rageClicks += metricNumber(experience.rageClicks);
      target.deadClicks += metricNumber(experience.deadClicks);
      target.quickbacks += metricNumber(experience.quickbacks);
      target.errors += metricNumber(experience.errors);
      target.hasCitationMetric = true;
      if (Array.isArray(row.providers)) {
        row.providers.forEach(provider => {
          const value = String(provider || '').trim();
          if (value && !target.providers.includes(value)) target.providers.push(value);
        });
      }
      if (gscImpressions || gscClicks) target.providers.push('gsc');
      if (bingImpressions || bingClicks) target.providers.push('bing_webmaster');
      if (metricNumber(behavior.sessions)) target.providers.push('ga4');
      if (metricNumber(experience.uxIssues)) target.providers.push('clarity');
      target.providers = Array.from(new Set(target.providers));
    });

    uxMetricRows.forEach(row => {
      const target = ensureForRow(row);
      if (!target) return;
      target.uxFriction += uxFrictionFromRow(row);
      if (Array.isArray(row.providers)) {
        row.providers.forEach(provider => {
          const value = String(provider || '').trim();
          if (value && !target.providers.includes(value)) target.providers.push(value);
        });
      }
    });

    return metrics;
  }, [pageMetricRows, uxMetricRows]);

  const loadAnalyticsSources = useCallback(async () => {
    if (!user || !projectId) {
      setAnalyticsSources([]);
      return;
    }

    const seq = ++analyticsStatusRequestSeq.current;
    const requestProjectId = projectId;
    setAnalyticsSourcesLoading(true);
    try {
      const token = await user.getIdToken();
      const response = await fetch(
        `/api/integrations/analytics/status?projectId=${encodeURIComponent(requestProjectId)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) return;
      const data = await response.json();
      if (seq !== analyticsStatusRequestSeq.current || projectIdRef.current !== requestProjectId)
        return;
      setAnalyticsSources(Array.isArray(data.sources) ? data.sources : []);
    } catch (error) {
      console.error('Failed to load analytics sources:', error);
    } finally {
      if (seq === analyticsStatusRequestSeq.current && projectIdRef.current === requestProjectId) {
        setAnalyticsSourcesLoading(false);
      }
    }
  }, [projectId, user]);

  useEffect(() => {
    loadAnalyticsSources();
  }, [loadAnalyticsSources]);

  const loadWebsitePerformanceMetrics = useCallback(
    async (urls: string[]) => {
      const analyticsUrls = Array.from(new Set(urls.map(normalizeAnalyticsUrlKey).filter(Boolean)));

      if (!user || !projectId || analyticsUrls.length === 0) {
        setPageMetricRows([]);
        setUxMetricRows([]);
        return;
      }

      const seq = ++websitePerformanceRequestSeq.current;
      const requestProjectId = projectId;
      const clearCurrentMetrics = () => {
        if (
          seq !== websitePerformanceRequestSeq.current ||
          projectIdRef.current !== requestProjectId
        )
          return;
        setPageMetricRows([]);
        setUxMetricRows([]);
      };

      try {
        const token = await user.getIdToken();
        const response = await fetch('/api/attune/website/digital-footprint', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            projectId: requestProjectId,
            crawlId: selectedCrawlId,
            startDate: effectivePageEvidenceStartDate,
            endDate: effectivePageEvidenceEndDate,
            urls: analyticsUrls,
            limit: analyticsUrls.length,
          }),
        });
        if (!response.ok) {
          clearCurrentMetrics();
          return;
        }
        const data = await response.json();
        if (
          seq !== websitePerformanceRequestSeq.current ||
          projectIdRef.current !== requestProjectId
        )
          return;
        setPageMetricRows(Array.isArray(data.pages) ? data.pages : []);
        setUxMetricRows([]);
      } catch (error) {
        console.error('Failed to load page analytics metrics:', error);
        clearCurrentMetrics();
      }
    },
    [projectId, effectivePageEvidenceEndDate, effectivePageEvidenceStartDate, selectedCrawlId, user]
  );

  // Load saved filters from Firestore
  const loadSavedFilters = useCallback(async () => {
    if (!user) return;
    try {
      const token = await user.getIdToken();
      const res = await fetch('/api/pages/filters', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const filters = await res.json();
        setSavedFilters(filters);
      }
    } catch (err) {
      console.error('Failed to load saved filters:', err);
    }
  }, [user]);

  useEffect(() => {
    loadSavedFilters();
  }, [loadSavedFilters]);

  // URL-based page IDs filter (from coverage analysis)
  const [urlFilteredIds, setUrlFilteredIds] = useState<Set<string> | null>(null);

  // Initialize filters from URL params
  useEffect(() => {
    const searchFromUrl = searchParams.get('search');
    if (searchFromUrl) {
      setSearchQuery(searchFromUrl);
    }

    // Handle ids parameter (comma-separated page IDs from coverage analysis)
    const idsFromUrl = searchParams.get('ids');
    if (idsFromUrl) {
      const ids = idsFromUrl.split(',').filter(id => id.trim());
      if (ids.length > 0) {
        setUrlFilteredIds(new Set(ids));
      }
    } else {
      setUrlFilteredIds(null);
    }
  }, [searchParams]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const openPageReview = useCallback(
    (page: ScrapedPage) => {
      if (!projectId) return;
      router.push(
        pageReviewHref({
          projectId,
          crawlId: page.crawl_id,
          pageId: page.id,
        })
      );
    },
    [projectId, router]
  );

  const loadCrawlStatus = useCallback(async () => {
    if (!user || !projectId) return;
    try {
      const seq = ++crawlStatusRequestSeq.current;
      const requestProjectId = projectId;
      const token = await user.getIdToken();
      const params = new URLSearchParams();
      if (brandDomain) params.set('brand_domain', brandDomain);
      if (requestProjectId) params.set('project_id', requestProjectId);
      const qs = params.toString();
      const url = qs ? `/api/pages/crawl-status?${qs}` : '/api/pages/crawl-status';

      const r = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) return;
      const data = await r.json();
      if (seq !== crawlStatusRequestSeq.current || projectIdRef.current !== requestProjectId)
        return;
      setCrawlStatus(data.crawl || null);
      setCrawlStatusProjectId(requestProjectId);
      const nextHistory = Array.isArray(data.crawls) ? (data.crawls as CrawlStatus[]) : [];
      const requestedCrawlId = searchParams.get('crawl') || '';
      setCrawlHistory(nextHistory);
      const currentCrawlId = selectedCrawlIdRef.current;
      const nextSelectedCrawlId =
        requestedCrawlId && nextHistory.some(crawl => crawl.crawl_id === requestedCrawlId)
          ? requestedCrawlId
          : currentCrawlId && nextHistory.some(crawl => crawl.crawl_id === currentCrawlId)
            ? currentCrawlId
            : data.crawl?.crawl_id || nextHistory[0]?.crawl_id || '';
      applyCrawlSelection(nextSelectedCrawlId);
      return data.crawl;
    } catch (e) {
      console.error('Failed to load crawl status:', e);
    }
  }, [user, brandDomain, projectId, searchParams, applyCrawlSelection]);

  const loadCrawlComparison = useCallback(
    async (crawlId: string) => {
      if (!user || !projectId || !crawlId) {
        setCrawlComparison(null);
        return;
      }
      const seq = ++crawlSnapshotRequestSeq.current;
      setCrawlComparisonLoading(true);
      try {
        const token = await user.getIdToken();
        const params = new URLSearchParams({ crawl_id: crawlId, project_id: projectId });
        const response = await fetch(`/api/pages/crawl-status?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) return;
        const data = await response.json();
        if (
          seq !== crawlSnapshotRequestSeq.current ||
          projectIdRef.current !== projectId ||
          crawlId !== selectedCrawlIdRef.current
        )
          return;
        setCrawlComparison(data.comparison || null);
      } catch (error) {
        console.error('Failed to load crawl comparison:', error);
        if (seq === crawlSnapshotRequestSeq.current) setCrawlComparison(null);
      } finally {
        if (seq === crawlSnapshotRequestSeq.current) setCrawlComparisonLoading(false);
      }
    },
    [projectId, user]
  );

  const loadStats = useCallback(
    async (options?: { crawlId?: string }) => {
      if (!user || !projectId) return;
      try {
        const seq = ++statsRequestSeq.current;
        const requestProjectId = projectId;
        const token = await user.getIdToken();
        // Filter stats by project's brand domain (same scoping as /api/pages list).
        const domain = brandDomain;
        const params = new URLSearchParams();
        if (requestProjectId) {
          params.set('project_id', requestProjectId);
        }
        const crawlId = options?.crawlId ?? selectedCrawlId;
        if (crawlId) {
          params.set('crawl_id', crawlId);
        }
        if (domain) {
          params.set('brand_domain', domain);
        }
        if (pathFilter && pathFilter !== '/') {
          params.set('path', pathFilter);
        }
        const r = await fetch(`/api/pages/stats?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) return;
        const data = await r.json();
        if (seq !== statsRequestSeq.current || projectIdRef.current !== requestProjectId) return;
        setPageStats(data);
        setPageStatsProjectId(requestProjectId);
      } catch (e) {
        console.error('Failed to load page stats:', e);
      }
    },
    [user, brandDomain, pathFilter, projectId, selectedCrawlId]
  );

  // Load pages with server-side pagination and filtering
  const loadPages = useCallback(
    async (options?: {
      append?: boolean;
      offset?: number;
      path?: string;
      search?: string;
      status?: string;
      depth?: string;
      grade?: string;
      crawlId?: string;
      // Sort options - pass directly to avoid stale closure issues
      sortBy?: string;
      sortOrder?: 'asc' | 'desc';
    }) => {
      if (!user || !projectId) return;
      const seq = ++pagesRequestSeq.current;
      const requestProjectId = projectId;
      const isLatestRequest = () =>
        seq === pagesRequestSeq.current && projectIdRef.current === requestProjectId;

      const isAppending = options?.append ?? false;
      setPagesError(null);
      if (isAppending) {
        setLoadingMore(true);
      } else {
        setTableLoading(true);
      }

      try {
        const token = await user.getIdToken();
        const params = new URLSearchParams({
          limit: String(PAGE_SIZE),
          offset: String(options?.offset ?? 0),
        });
        if (requestProjectId) {
          params.set('project_id', requestProjectId);
        }
        const crawlId = options?.crawlId ?? selectedCrawlId;
        if (crawlId) {
          params.set('crawl_id', crawlId);
        }

        // Filter by project's brand domain to show only pages from this project's website
        const domain = brandDomain;
        if (domain) {
          params.set('brand_domain', domain);
        }

        // Apply server-side filters
        const pathVal = options?.path ?? pathFilter;
        const searchVal = options?.search ?? debouncedSearch;
        const statusVal = options?.status ?? statusFilter;
        const depthVal = options?.depth ?? depthFilter;
        const gradeVal = options?.grade ?? scoreFilter; // scoreFilter now maps to server-side grade

        if (pathVal && pathVal !== '/') params.set('path', pathVal);
        if (searchVal) params.set('search', searchVal);
        if (statusVal) params.set('status', statusVal);
        if (depthVal) params.set('depth', depthVal);
        if (gradeVal) params.set('grade', gradeVal);

        // Sort at the API so pagination preserves one stable order across the full result set.
        const activeSortBy = options?.sortBy ?? analyticsSortApiColumn(analyticsSortColumn);
        if (activeSortBy) {
          params.set('sort_by', activeSortBy);
          params.set('sort_order', options?.sortOrder || analyticsSortOrder);
          if (activeSortBy === 'search_impressions' || activeSortBy === 'search_clicks') {
            params.set('start_date', selectedCrawlDate || websiteDateRange.startDate);
            params.set('end_date', selectedCrawlDate || websiteDateRange.endDate);
          }
        }

        const r = await fetch(`/api/pages?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) throw new Error(`Failed to load pages (${r.status})`);
        const data = await r.json();

        // Ignore out-of-order responses (e.g. project switching)
        if (!isLatestRequest()) return;

        if (isAppending) {
          setPages(prev => [...prev, ...(data.pages || [])]);
        } else {
          setPages(data.pages || []);
        }
        setPagesProjectId(requestProjectId);
        setTotal(data.total || 0);
        setHasMore(data.has_more || false);
        // Store server-provided grade distribution for current filter context
        if (data.gradeDistribution) {
          setServerGradeDistribution(data.gradeDistribution);
        }
      } catch (e) {
        if (isLatestRequest()) {
          setPagesError({
            projectId: requestProjectId,
            message: e instanceof Error ? e.message : 'Unable to load pages.',
            append: isAppending,
          });
        }
      } finally {
        if (isLatestRequest()) {
          setTableLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [
      user,
      projectId,
      brandDomain,
      pathFilter,
      debouncedSearch,
      statusFilter,
      depthFilter,
      scoreFilter,
      selectedCrawlId,
      analyticsSortColumn,
      analyticsSortOrder,
      selectedCrawlDate,
      websiteDateRange.endDate,
      websiteDateRange.startDate,
    ]
  );

  useEffect(() => {
    const seq = ++pageReviewQueueRequestSeq.current;
    const pageIds = pages.map(page => page.id);
    if (
      !user ||
      !projectId ||
      !selectedCrawlId ||
      activeWebsiteTab !== 'map' ||
      pageIds.length === 0
    ) {
      setPageReviewQueue([]);
      return;
    }
    const pageIdBatches = Array.from({ length: Math.ceil(pageIds.length / 100) }, (_, index) =>
      pageIds.slice(index * 100, (index + 1) * 100)
    );
    void (async () => {
      const rows: PageReviewQueuePage[] = [];
      for (const batch of pageIdBatches) {
        const payload = await callPageReview<PageReviewQueuePayload>(user, 'queue', {
          projectId,
          crawlId: selectedCrawlId,
          pageIds: batch,
          limit: batch.length,
        });
        rows.push(...(payload.pages || []));
      }
      if (seq !== pageReviewQueueRequestSeq.current) return;
      setPageReviewQueue(Array.from(new Map(rows.map(row => [row.id, row])).values()));
    })().catch(error => {
      if (seq !== pageReviewQueueRequestSeq.current) return;
      console.error('Failed to load Page Review queue:', error);
      setPageReviewQueue([]);
    });
  }, [activeWebsiteTab, pages, projectId, selectedCrawlId, user]);

  // Load more pages (pagination)
  const loadMorePages = useCallback(() => {
    if (loadingMore || !visibleHasMore) return;
    loadPages({ append: true, offset: visiblePages.length });
  }, [loadPages, loadingMore, visibleHasMore, visiblePages.length]);

  // Reload pages when server-side filters change
  const prevFiltersRef = useRef({
    pathFilter: '',
    debouncedSearch: '',
    statusFilter: '',
    depthFilter: '',
    scoreFilter: '',
  });
  useEffect(() => {
    // Skip on initial load (handled separately)
    if (initialLoading) return;

    const prev = prevFiltersRef.current;
    const filtersChanged =
      prev.pathFilter !== pathFilter ||
      prev.debouncedSearch !== debouncedSearch ||
      prev.statusFilter !== statusFilter ||
      prev.depthFilter !== depthFilter ||
      prev.scoreFilter !== scoreFilter;

    if (filtersChanged) {
      const pathChanged = prev.pathFilter !== pathFilter;
      prevFiltersRef.current = {
        pathFilter,
        debouncedSearch,
        statusFilter,
        depthFilter,
        scoreFilter,
      };
      loadPages({ offset: 0 });
      if (pathChanged) {
        loadStats();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathFilter, debouncedSearch, statusFilter, depthFilter, scoreFilter, initialLoading]);

  const previousAnalyticsSortRef = useRef({
    column: analyticsSortColumn,
    order: analyticsSortOrder,
    crawlId: selectedCrawlId,
    crawlDate: selectedCrawlDate,
  });
  useEffect(() => {
    const previousSort = previousAnalyticsSortRef.current;
    const sortChanged =
      previousSort.column !== analyticsSortColumn || previousSort.order !== analyticsSortOrder;
    const crawlChanged = previousSort.crawlId !== selectedCrawlId;
    const crawlDateChanged = previousSort.crawlDate !== selectedCrawlDate;
    previousAnalyticsSortRef.current = {
      column: analyticsSortColumn,
      order: analyticsSortOrder,
      crawlId: selectedCrawlId,
      crawlDate: selectedCrawlDate,
    };
    if (
      (!sortChanged && (!crawlDateChanged || crawlChanged)) ||
      !analyticsSortColumn ||
      initialLoading ||
      !projectScopeReady
    )
      return;
    void loadPages({ offset: 0 });
  }, [
    analyticsSortColumn,
    analyticsSortOrder,
    initialLoading,
    loadPages,
    projectScopeReady,
    selectedCrawlDate,
    selectedCrawlId,
  ]);

  // Filter pages client-side (only for filters not supported server-side)
  const filteredPages = useMemo(() => {
    let result = visiblePages;

    // URL-based IDs filter (from coverage analysis links)
    if (urlFilteredIds && urlFilteredIds.size > 0) {
      result = result.filter(p => urlFilteredIds.has(p.id));
    }

    // Note: ALL filters (path, depth, search, status, score/grade) are now server-side
    // No additional client-side filtering needed for these

    return result;
  }, [visiblePages, urlFilteredIds]);

  const displayedAnalyticsUrls = useMemo(
    () =>
      filteredPages
        .map(page => page.normalized_url || page.url)
        .filter((url): url is string => Boolean(url)),
    [filteredPages]
  );

  useEffect(() => {
    loadWebsitePerformanceMetrics(displayedAnalyticsUrls);
  }, [displayedAnalyticsUrls, loadWebsitePerformanceMetrics]);

  const sortedPages = useMemo(() => {
    if (!analyticsSortColumn) return filteredPages;

    const metricValue = (page: ScrapedPage) => {
      const analytics =
        pageAnalyticsByUrl.get(normalizeAnalyticsUrlKey(page.normalized_url)) ||
        pageAnalyticsByUrl.get(normalizeAnalyticsUrlKey(page.url));
      if (analyticsSortColumn === 'citations') {
        return page.citation_count;
      }
      return analyticsSortColumn === 'impressions'
        ? analytics?.searchImpressions || 0
        : analytics?.searchClicks || 0;
    };

    return [...filteredPages].sort((a, b) => {
      const metricDelta = metricValue(a) - metricValue(b);
      if (metricDelta !== 0) {
        return analyticsSortOrder === 'asc' ? metricDelta : -metricDelta;
      }
      return a.url.localeCompare(b.url);
    });
  }, [analyticsSortColumn, analyticsSortOrder, filteredPages, pageAnalyticsByUrl]);

  const pageReviewQueueById = useMemo(
    () => new Map(pageReviewQueue.map(item => [item.id, item])),
    [pageReviewQueue]
  );

  const inventoryRows = useMemo<PageInventoryRow<ScrapedPage>[]>(
    () =>
      sortedPages.map(page => {
        const analytics =
          pageAnalyticsByUrl.get(normalizeAnalyticsUrlKey(page.normalized_url)) ||
          pageAnalyticsByUrl.get(normalizeAnalyticsUrlKey(page.url));
        const queuePage = pageReviewQueueById.get(page.id);
        return {
          id: page.id,
          crawlId: page.crawl_id,
          url: page.url,
          title: page.title,
          description: page.description,
          ogImage: page.og_image,
          seoGrade: page.seo_grade,
          wordCount: page.word_count,
          httpStatus: page.http_status,
          citationCount: page.citation_count,
          searchImpressions: analytics?.searchImpressions || queuePage?.search_impressions || 0,
          searchClicks: analytics?.searchClicks || queuePage?.search_clicks || 0,
          sessions: analytics?.sessions || queuePage?.sessions || 0,
          opportunityLabel: queuePage?.opportunity_label || null,
          hasHtml: page.artifact_status === 'complete' || Boolean(queuePage),
          review: queuePage?.review || null,
          source: page,
        };
      }),
    [pageAnalyticsByUrl, pageReviewQueueById, sortedPages]
  );

  // Calculate average SEO score for filtered pages (using pre-calculated scores)

  // Save current filter to Firestore
  const saveCurrentFilter = async () => {
    if (!user || !newFilterName.trim()) {
      toast.error('Please enter a filter name');
      return;
    }

    setSavingFilter(true);
    try {
      const token = await user.getIdToken();

      const idsToSave = urlFilteredIds ? Array.from(urlFilteredIds) : null;

      const res = await fetch('/api/pages/filters', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: newFilterName.trim(),
          pathFilter,
          depthFilter,
          scoreFilter,
          statusFilter,
          searchQuery,
          pageIds: idsToSave,
        }),
      });

      if (res.ok) {
        const newFilter = await res.json();
        setSavedFilters(prev => [newFilter, ...prev]);
        setSelectedSavedFilterId(newFilter.id);
        setNewFilterName('');
        setShowSaveDialog(false);
        toast.success(`Filter "${newFilter.name}" saved!`);
      } else {
        throw new Error('Failed to save filter');
      }
    } catch (err) {
      console.error('Failed to save filter:', err);
      toast.error('Failed to save filter');
    } finally {
      setSavingFilter(false);
    }
  };

  // Load a saved filter
  const loadSavedFilter = (filter: SavedFilter) => {
    setSelectedSavedFilterId(filter.id);
    setPathFilter(filter.pathFilter);
    setDepthFilter(filter.depthFilter);
    setScoreFilter(filter.scoreFilter);
    setStatusFilter(filter.statusFilter);
    setSearchQuery(filter.searchQuery);

    if (filter.pageIds && filter.pageIds.length > 0) {
      setUrlFilteredIds(new Set(filter.pageIds));
    } else {
      setUrlFilteredIds(null);
    }

    toast.success(`Filter "${filter.name}" applied`);
  };

  // Delete a saved filter from Firestore
  const confirmDeleteFilter = async () => {
    if (!user || !filterToDelete) return;

    try {
      const token = await user.getIdToken();
      const res = await fetch(`/api/pages/filters?id=${filterToDelete.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        setSavedFilters(prev => prev.filter(f => f.id !== filterToDelete.id));
        setSelectedSavedFilterId(prev => (prev === filterToDelete.id ? '' : prev));
        toast.success('Filter deleted');
      } else {
        throw new Error('Failed to delete filter');
      }
    } catch (err) {
      console.error('Failed to delete filter:', err);
      toast.error('Failed to delete filter');
    } finally {
      setFilterToDelete(null);
    }
  };

  const startCrawl = async () => {
    if (!user) return;
    setIsStartingCrawl(true);
    try {
      const token = await user.getIdToken();
      const r = await fetch('/api/pages/crawl', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          project_id: currentProject?.id || undefined,
          brand_domain: brandDomain || undefined,
        }),
      });
      const data = await r.json();
      if (!r.ok) {
        throw new Error(data.error || 'Failed to start crawl');
      }
      toast.success('Website indexing started! This may take several hours.');
      if (data.crawl_id) {
        handleCrawlSelection(data.crawl_id);
        applyCrawlSelection(data.crawl_id);
      }
      await loadCrawlStatus();
      startPolling();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to start crawl');
    } finally {
      setIsStartingCrawl(false);
    }
  };

  const loadCrawlStatusRef = useRef(loadCrawlStatus);
  const loadPagesRef = useRef(loadPages);
  const loadStatsRef = useRef(loadStats);
  const loadCrawlComparisonRef = useRef(loadCrawlComparison);
  useEffect(() => {
    loadCrawlStatusRef.current = loadCrawlStatus;
    loadPagesRef.current = loadPages;
    loadStatsRef.current = loadStats;
    loadCrawlComparisonRef.current = loadCrawlComparison;
  }, [loadCrawlComparison, loadCrawlStatus, loadPages, loadStats]);

  const startPolling = useCallback(() => {
    if (pollIntervalRef.current) return;
    pollIntervalRef.current = setInterval(async () => {
      const status = await loadCrawlStatusRef.current();
      if (status?.status === 'completed' || status?.status === 'failed') {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        const completedCrawlIsSelected = selectedCrawlIdRef.current === status.crawl_id;
        if (completedCrawlIsSelected) {
          await loadPagesRef.current({ crawlId: status.crawl_id });
          if (status.status === 'completed') {
            await loadCrawlComparisonRef.current(status.crawl_id);
          }
        }
        if (status.status === 'completed') {
          toast.success(`Indexing complete! ${status.page_count} pages found.`);
        } else if (status.status === 'failed') {
          toast.error(`Indexing failed: ${status.error || 'Unknown error'}`);
        }
      }
    }, 5000);
  }, []);

  useEffect(
    () => () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    },
    []
  );

  // Preserve a deep-linked crawl on mount, but clear it before loading another project.
  const selectionProjectIdRef = useRef(projectId);
  useEffect(() => {
    if (selectionProjectIdRef.current === projectId) return;
    selectionProjectIdRef.current = projectId;
    setSelectedSavedFilterId('');
    setCrawlHistory([]);
    selectedCrawlIdRef.current = '';
    setSelectedCrawlId('');
    setCrawlComparison(null);
  }, [projectId]);

  // Initial hydration and later project changes share the same lifecycle. A project
  // can reconcile while its predecessor is still loading; it must start its own
  // requests without waiting for (or accepting completion from) the old scope.
  useEffect(() => {
    if (!user || projectLoading || !projectsReady) return;
    if (!projectId) {
      setInitialLoading(false);
      setTableLoading(false);
      return;
    }

    let cancelled = false;
    setInitialLoading(true);
    const init = async () => {
      const initialRequestCrawlId = selectedCrawlIdRef.current;
      const statusPromise = loadCrawlStatusRef.current();
      void loadStatsRef.current({ crawlId: initialRequestCrawlId });
      await loadPagesRef.current({ crawlId: initialRequestCrawlId });
      const status = await statusPromise;
      if (cancelled) return;
      const normalizedCrawlId = selectedCrawlIdRef.current;
      skipInitialCrawlReloadRef.current =
        !initialRequestCrawlId || initialRequestCrawlId === normalizedCrawlId
          ? normalizedCrawlId
          : '';
      setInitialLoading(false);

      if (status?.status === 'pending' || status?.status === 'running') {
        startPolling();
      }
    };

    void init();
    return () => {
      cancelled = true;
      pagesRequestSeq.current += 1;
      statsRequestSeq.current += 1;
      crawlStatusRequestSeq.current += 1;
      crawlSnapshotRequestSeq.current += 1;
      setCrawlComparisonLoading(false);
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [user, projectLoading, projectsReady, projectId, brandDomain, startPolling]);

  useEffect(() => {
    if (!selectedCrawlId || initialLoading || !projectScopeReady) return;
    if (skipInitialCrawlReloadRef.current === selectedCrawlId) {
      skipInitialCrawlReloadRef.current = '';
      void loadCrawlComparison(selectedCrawlId);
      return;
    }
    void loadPages({ offset: 0 });
    void loadStats();
    void loadCrawlComparison(selectedCrawlId);
    // Crawl selection is the only trigger here; filters have their own reload effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCrawlId, initialLoading, projectScopeReady]);

  const isActiveCrawl =
    visibleCrawlStatus?.status === 'pending' || visibleCrawlStatus?.status === 'running';
  const hasPages = visiblePages.length > 0 || visibleTotal > 0;
  const canRestartCrawl = hasPages || crawlHistory.length > 0;
  const isRetryingFailedCrawl = visibleCrawlStatus?.status === 'failed' && !hasPages;
  const hasActiveFilters =
    pathFilter || depthFilter || scoreFilter || statusFilter || debouncedSearch || urlFilteredIds;
  // Only show "start indexing" before the project has any crawl history. Empty or failed
  // snapshots must keep the crawl selector visible so users can return to an older crawl.
  const showEmptyState =
    projectScopeReady &&
    !initialLoading &&
    !tableLoading &&
    crawlHistory.length === 0 &&
    !hasPages &&
    !isActiveCrawl &&
    !hasActiveFilters;

  const clearSavedFilterSelection = useCallback(() => {
    setSelectedSavedFilterId('');
  }, []);

  // Clear all filters
  const clearFilters = () => {
    setPathFilter('');
    setDepthFilter('');
    setScoreFilter('');
    setStatusFilter('');
    setSearchQuery('');
    setUrlFilteredIds(null);
    setSelectedSavedFilterId('');
    const nextSearchParams = new URLSearchParams(searchParams.toString());
    for (const key of ['search', 'ids', 'path', 'depth', 'grade', 'status']) {
      nextSearchParams.delete(key);
    }
    const query = nextSearchParams.toString();
    router.replace(`/app/pages${query ? `?${query}` : ''}`, { scroll: false });
  };

  // Get available path segments for filter
  const pathSegments = useMemo(() => {
    const segments = new Map<string, number>();
    visiblePages.forEach(p => {
      const path = getUrlPath(p.url);
      const parts = path.split('/').filter(Boolean);
      if (parts.length > 0) {
        const firstSeg = `/${parts[0]}`;
        segments.set(firstSeg, (segments.get(firstSeg) || 0) + 1);
      } else {
        segments.set('/', (segments.get('/') || 0) + 1);
      }
    });
    return Array.from(segments.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([seg, count]) => ({ value: seg, label: `${seg} (${count})` }));
  }, [visiblePages]);

  // Get unique depths for filter
  const availableDepths = useMemo(() => {
    const depths = new Set<number>();
    visiblePages.forEach(p => {
      const path = getUrlPath(p.url);
      depths.add(getPathDepth(path));
    });
    return Array.from(depths)
      .sort((a, b) => a - b)
      .map(d => ({
        value: String(d),
        label: d === 0 ? 'Root (/)' : `Level ${d}`,
      }));
  }, [visiblePages]);

  // Grid template: Preview, Score, Title, URL, Words, Status, normalized analytics.
  const compactGradeOptions = useMemo(() => {
    const hasNonScoreFilters =
      pathFilter || depthFilter || statusFilter || debouncedSearch || urlFilteredIds;
    return [
      { value: '', label: 'All grades' },
      ...(['A', 'B', 'C', 'D', 'F'] as const).map(grade => {
        const globalCount = visiblePageStats?.scoreDistribution[grade] || 0;
        const count = hasNonScoreFilters
          ? (serverGradeDistribution?.[grade] ?? globalCount)
          : globalCount;
        return { value: grade, label: `${grade} (${count})` };
      }),
    ];
  }, [
    debouncedSearch,
    depthFilter,
    pathFilter,
    serverGradeDistribution,
    statusFilter,
    urlFilteredIds,
    visiblePageStats,
  ]);
  const savedFilterOptions = useMemo(
    () => [
      { value: '', label: 'Saved filters' },
      ...savedFilters.map(filter => ({ value: filter.id, label: filter.name })),
    ],
    [savedFilters]
  );
  const crawlDateOptions = useMemo(() => {
    const dateCounts = crawlHistory.reduce((counts, crawl) => {
      const date = crawlTimestamp(crawl)?.slice(0, 10) || '';
      counts.set(date, (counts.get(date) || 0) + 1);
      return counts;
    }, new Map<string, number>());
    return crawlHistory.map(crawl => {
      const date = crawlTimestamp(crawl)?.slice(0, 10) || '';
      const includeTime = (dateCounts.get(date) || 0) > 1;
      const progressLabel =
        crawl.status === 'running' || crawl.status === 'pending'
          ? `Crawling · ${crawl.page_count.toLocaleString()} found`
          : `${crawl.page_count.toLocaleString()} pages`;
      return {
        value: crawl.crawl_id,
        label: `${formatCrawlDate(crawl, includeTime)} · ${progressLabel}`,
      };
    });
  }, [crawlHistory]);
  const mapFilterPanel =
    activeWebsiteTab === 'map' && !showEmptyState ? (
      <FilterBar data-section="pagesCompactFilters">
        <InlineFilterGroup
          label="Crawl date"
          helpText="Choose a completed or active website crawl"
          options={crawlDateOptions}
          value={selectedCrawlId}
          onChange={handleCrawlSelection}
          placeholder="No crawls"
          disabled={crawlDateOptions.length === 0}
          hideLabel
        />
        <InlineFilterGroup
          label="Section"
          helpText="Limit results to a URL section"
          options={[{ value: '', label: `All pages (${visibleTotal})` }, ...pathSegments]}
          value={pathFilter}
          onChange={value => {
            clearSavedFilterSelection();
            setPathFilter(value);
          }}
          hideLabel
        />

        <FilterPopover
          activeCount={[scoreFilter, depthFilter, statusFilter].filter(Boolean).length}
        >
          <VStack align="stretch" gap={3}>
            <InlineFilterGroup
              label="Grade"
              helpText="Filter by Attune page score grade"
              options={compactGradeOptions}
              value={scoreFilter}
              onChange={value => {
                clearSavedFilterSelection();
                setScoreFilter(value);
              }}
              hideLabel
            />
            <InlineFilterGroup
              label="Depth"
              helpText="Filter by URL path depth"
              options={[{ value: '', label: 'All depths' }, ...availableDepths]}
              value={depthFilter}
              onChange={value => {
                clearSavedFilterSelection();
                setDepthFilter(value);
              }}
              hideLabel
            />
            <InlineFilterGroup
              label="Status"
              helpText="Filter by the latest HTTP response status"
              options={[
                { value: '', label: 'All statuses' },
                { value: '200', label: '200 OK' },
                { value: '301', label: '301 Redirect' },
                { value: '404', label: '404 Not Found' },
                { value: '500', label: '500 Error' },
              ]}
              value={statusFilter}
              onChange={value => {
                clearSavedFilterSelection();
                setStatusFilter(value);
              }}
              hideLabel
            />
            {savedFilters.length > 0 && (
              <InlineFilterGroup
                label="Saved"
                helpText="Load a reusable saved filter set"
                options={savedFilterOptions}
                value={selectedSavedFilterId}
                onChange={filterId => {
                  if (!filterId) {
                    clearFilters();
                    return;
                  }
                  const filter = savedFilters.find(savedFilter => savedFilter.id === filterId);
                  if (filter) loadSavedFilter(filter);
                }}
                hideLabel
              />
            )}
          </VStack>
        </FilterPopover>
        <HStack gap={1.5} flexShrink={0} ml="auto">
          {hasActiveFilters && (
            <FilterPill onClick={() => setShowSaveDialog(true)}>
              <Box as="span" display="inline-flex" me="1.5">
                <Save size={14} />
              </Box>
              Save
            </FilterPill>
          )}
          {selectedSavedFilterId && (
            <FilterPill
              color="status.danger.fg"
              onClick={() => {
                const filter = savedFilters.find(
                  savedFilter => savedFilter.id === selectedSavedFilterId
                );
                if (filter) setFilterToDelete(filter);
              }}
            >
              <Box as="span" display="inline-flex" me="1.5">
                <Trash2 size={14} />
              </Box>
              Delete
            </FilterPill>
          )}
          {hasActiveFilters && <FilterClearButton onClick={clearFilters} />}
        </HStack>
      </FilterBar>
    ) : null;

  return (
    <Box
      px={{ base: 2, md: 4, '2xl': 6 }}
      py={{ base: 2, md: 3 }}
      w="full"
      maxW="1480px"
      mx="auto"
      minW={0}
      overflowX="hidden"
    >
      <AppShellToolbarPortal
        actions={
          activeWebsiteTab === 'map' && canRestartCrawl && !isActiveCrawl ? (
            <Button
              onClick={startCrawl}
              disabled={isStartingCrawl}
              variant="ghost"
              size="sm"
              minW="9"
              px={{ base: '2', xl: '3' }}
              aria-label={isRetryingFailedCrawl ? 'Retry website indexing' : 'Re-index website'}
              title={isRetryingFailedCrawl ? 'Retry website indexing' : 'Re-index website'}
            >
              <Box as="span" display="inline-flex">
                <RefreshCw size={16} />
              </Box>
            </Button>
          ) : null
        }
        filters={
          activeWebsiteTab === 'map' ? (
            mapFilterPanel
          ) : (
            <WebsiteSharedControls
              projects={projectOptions}
              currentProjectId={projectId}
              onProjectChange={nextProjectId => {
                if (nextProjectId && nextProjectId !== projectId) {
                  switchProject(nextProjectId);
                }
              }}
              brandDomain={brandDomain}
              dateRange={websiteDateRange}
              onDateRangeChange={handleWebsiteDateRangeChange}
              selectedPath={pathFilter || '/'}
              onPathChange={path => {
                clearSavedFilterSelection();
                setPathFilter(path === '/' ? '' : path);
              }}
              siteTree={visiblePageStats?.siteTree || null}
              sources={analyticsSources}
              sourcesLoading={analyticsSourcesLoading}
              showIntegrationControls
              variant="toolbar"
            />
          )
        }
      />

      <Dialog.Root open={showSaveDialog} onOpenChange={e => setShowSaveDialog(e.open)}>
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>
                <Dialog.Title>Save Filter</Dialog.Title>
              </Dialog.Header>
              <Dialog.Body>
                <VStack align="stretch" gap={3}>
                  <Text fontSize="sm" color="ui.text.secondary">
                    Save the current Pages filters for quick reuse.
                  </Text>
                  <Input
                    value={newFilterName}
                    onChange={event => setNewFilterName(event.target.value)}
                    placeholder="Filter name"
                    autoFocus
                  />
                </VStack>
              </Dialog.Body>
              <Dialog.Footer>
                <Dialog.ActionTrigger asChild>
                  <Button variant="outline">Cancel</Button>
                </Dialog.ActionTrigger>
                <Button
                  onClick={saveCurrentFilter}
                  loading={savingFilter}
                  disabled={!newFilterName.trim()}
                >
                  Save filter
                </Button>
              </Dialog.Footer>
              <Dialog.CloseTrigger asChild>
                <CloseButton size="sm" />
              </Dialog.CloseTrigger>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>

      <Dialog.Root open={!!filterToDelete} onOpenChange={e => !e.open && setFilterToDelete(null)}>
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>
                <Dialog.Title>Delete Filter</Dialog.Title>
              </Dialog.Header>
              <Dialog.Body>
                <Text>
                  Are you sure you want to delete{' '}
                  <strong>&quot;{filterToDelete?.name}&quot;</strong>? This action cannot be undone.
                </Text>
              </Dialog.Body>
              <Dialog.Footer>
                <Dialog.ActionTrigger asChild>
                  <Button variant="outline">Cancel</Button>
                </Dialog.ActionTrigger>
                <Button colorPalette="red" onClick={confirmDeleteFilter}>
                  Delete
                </Button>
              </Dialog.Footer>
              <Dialog.CloseTrigger asChild>
                <CloseButton size="sm" />
              </Dialog.CloseTrigger>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>

      <VStack align="stretch" gap={{ base: 3, md: 4 }} minW={0}>
        {/* Active Crawl Progress */}
        {isActiveCrawl && visibleCrawlStatus && (
          <Box
            layerStyle="dataTableSurface"
            boxShadow="none"
            w="full"
            p={6}
            borderRadius="0"
            borderWidth={{ base: '1px', md: '0' }}
          >
            <HStack gap={4}>
              <InlineSpinner size="md" />
              <VStack align="start" gap={1}>
                <Text fontWeight="500" color="ui.text.primary">
                  Indexing your website…
                </Text>
                <Text fontSize="sm" color="ui.text.secondary">
                  {visibleCrawlStatus.page_count > 0
                    ? `${visibleCrawlStatus.page_count} pages found so far`
                    : 'Starting crawl…'}
                </Text>
                <Text fontSize="xs" color="ui.text.tertiary">
                  Domain: {visibleCrawlStatus.brand_domain} • This may take several hours for large
                  sites
                </Text>
              </VStack>
            </HStack>
          </Box>
        )}

        {/* Empty State */}
        {activeWebsiteTab === 'map' && showEmptyState && (
          <Box
            layerStyle="dataTableSurface"
            boxShadow="none"
            w="full"
            p={12}
            borderRadius="0"
            borderWidth={{ base: '1px', md: '0' }}
          >
            <VStack gap={6}>
              <Box
                p={4}
                bg="linear-gradient(135deg, var(--chakra-colors-status-success-fg) 0%, var(--chakra-colors-status-success-fg) 100%)"
                borderRadius="full"
              >
                <Globe size={40} color="white" />
              </Box>
              <VStack gap={2}>
                <Text fontSize="xl" fontWeight="600" color="ui.text.primary">
                  Index Your Website
                </Text>
                <Text fontSize="sm" color="ui.text.secondary" textAlign="center" maxW="400px">
                  Let us crawl your website to analyze your content structure, review search,
                  traffic, AI citation, and content health signals.
                </Text>
              </VStack>
              <Button
                onClick={startCrawl}
                disabled={isStartingCrawl}
                bg="linear-gradient(135deg, var(--chakra-colors-status-success-fg) 0%, var(--chakra-colors-status-success-fg) 100%)"
                color="white"
                size="lg"
                _hover={{ opacity: 0.9 }}
              >
                {isStartingCrawl ? (
                  <>
                    <InlineSpinner size="xs" />
                    <Box ml={2}>Starting…</Box>
                  </>
                ) : (
                  <>
                    <Box as="span" display="inline-flex" me="2">
                      <Globe size={20} />
                    </Box>
                    Start Indexing
                  </>
                )}
              </Button>
              <Text fontSize="xs" color="ui.text.tertiary">
                Typically takes 1-4 hours depending on site size
              </Text>
            </VStack>
          </Box>
        )}

        {/* Page Inventory */}
        {activeWebsiteTab === 'map' && !showEmptyState && (
          <Grid
            templateColumns={{ base: 'minmax(0, 1fr)', xl: 'minmax(0, 1fr) 280px' }}
            gap={{ base: 5, xl: 8 }}
            alignItems="start"
            w="full"
          >
            <VStack data-section="pagesOpportunityMidrail" align="stretch" gap={5} minW={0}>
              <Box
                w="full"
                bg="transparent"
                overflow="visible"
                display="flex"
                flexDirection="column"
                minW={0}
              >
                {/* Toolbar */}
                <Box px={0} py={1} bg="transparent">
                  <Flex
                    align={{ base: 'stretch', md: 'center' }}
                    gap={2}
                    minW={0}
                    direction={{ base: 'column', md: 'row' }}
                  >
                    <Text fontSize="sm" fontWeight="700" color="ui.text.primary">
                      Pages
                    </Text>
                    <Box
                      position="relative"
                      w={{ base: 'full', md: '260px' }}
                      ml={{ base: 0, md: 'auto' }}
                    >
                      <Box
                        position="absolute"
                        left="10px"
                        top="50%"
                        transform="translateY(-50%)"
                        zIndex={1}
                      >
                        <Search size={14} color="var(--chakra-colors-ui-text-tertiary)" />
                      </Box>
                      <Input
                        h="34px"
                        pl="32px"
                        placeholder="Search pages"
                        value={searchQuery}
                        onChange={e => {
                          clearSavedFilterSelection();
                          setSearchQuery(e.target.value);
                        }}
                        bg="ui.surface.input"
                        borderWidth="1px"
                        borderStyle="solid"
                        borderColor="ui.border.subtle"
                        borderRadius="glassInput"
                        fontSize="sm"
                        _focus={{
                          borderColor: 'ui.border.default',
                          boxShadow: 'none',
                        }}
                      />
                    </Box>
                    <Text fontSize="xs" color="ui.text.secondary" whiteSpace="nowrap">
                      {tableLoading && filteredPages.length === 0
                        ? 'Loading pages…'
                        : `${filteredPages.length} of ${visibleTotal} pages`}
                    </Text>
                  </Flex>
                </Box>

                <Box flex={{ base: 'initial', md: 1 }} minH={0} overflow="visible">
                  {visiblePagesError && (
                    <Box role="alert" p={4}>
                      <Text color="status.danger.fg">{visiblePagesError.message}</Text>
                      <Button
                        size="sm"
                        variant="outline"
                        mt={2}
                        onClick={() => {
                          if (visiblePagesError.append) loadMorePages();
                          else void loadPages();
                        }}
                        disabled={tableLoading || loadingMore}
                      >
                        Retry loading pages
                      </Button>
                    </Box>
                  )}
                  <PagesInventoryTable
                    rows={inventoryRows}
                    loading={tableLoading}
                    sortColumn={analyticsSortColumn}
                    sortOrder={analyticsSortOrder}
                    onSort={toggleAnalyticsSort}
                    onOpenPage={row => openPageReview(row.source)}
                  />

                  {filteredPages.length === 0 && !tableLoading && !visiblePagesError && (
                    <Box py={8} textAlign="center">
                      <Text color="ui.text.secondary">
                        {hasActiveFilters ? 'No pages match your filters.' : 'No pages found.'}
                      </Text>
                      {hasActiveFilters && (
                        <Button
                          size="sm"
                          variant="ghost"
                          color="accent.fg"
                          _hover={{ bg: 'accent.subtle' }}
                          mt={2}
                          onClick={clearFilters}
                        >
                          Clear filters
                        </Button>
                      )}
                    </Box>
                  )}

                  {visibleHasMore && filteredPages.length > 0 && (
                    <Box
                      py={4}
                      textAlign="center"
                      borderTopWidth="1px"
                      borderColor="ui.border.subtle"
                    >
                      <Button
                        size="sm"
                        variant="outline"
                        colorPalette="green"
                        onClick={loadMorePages}
                        loading={loadingMore}
                        disabled={loadingMore}
                      >
                        {loadingMore
                          ? 'Loading…'
                          : `Load More (${visiblePages.length} of ${visibleTotal})`}
                      </Button>
                    </Box>
                  )}
                </Box>

                {/* Footer */}
                {filteredPages.length > 0 && visibleHasMore && (
                  <Box
                    px={4}
                    py={2.5}
                    borderTopWidth="1px"
                    borderTopStyle="solid"
                    borderColor="ui.border.subtle"
                    bg="transparent"
                  >
                    <HStack justify="space-between">
                      <Text fontSize="xs" color="ui.text.secondary">
                        Showing {filteredPages.length} of {visibleTotal} pages
                        {hasActiveFilters && ` (filtered)`}
                      </Text>
                      <Text fontSize="xs" color="status.success.fg" fontWeight="500">
                        {visibleTotal - visiblePages.length} more available
                      </Text>
                    </HStack>
                  </Box>
                )}
              </Box>
            </VStack>

            <Box
              data-section="pagesContextRightRail"
              bg="transparent"
              position={{ base: 'static', xl: 'sticky' }}
              top={{ xl: 3 }}
            >
              <VStack align="stretch" gap={4}>
                <Box>
                  <HStack gap={2}>
                    <Gauge size={16} color="var(--chakra-colors-ui-text-secondary)" />
                    <Text fontSize="sm" fontWeight="700" color="ui.text.primary">
                      Page context
                    </Text>
                  </HStack>
                  <Text mt={1} fontSize="xs" color="ui.text.secondary">
                    {selectedCrawl
                      ? `${formatCrawlDate(selectedCrawl)} · ${selectedCrawl.page_count.toLocaleString()} pages`
                      : 'Select a crawl to review its snapshot'}
                  </Text>
                </Box>

                {crawlComparisonLoading ? (
                  <HStack gap={2} py={3} color="ui.text.secondary">
                    <InlineSpinner size="xs" />
                    <Text fontSize="sm">Comparing crawls…</Text>
                  </HStack>
                ) : crawlComparison ? (
                  <Box>
                    <Text fontSize="xs" color="ui.text.tertiary" mb={2}>
                      Since {previousCrawl ? formatCrawlDate(previousCrawl) : 'the previous crawl'}
                    </Text>
                    <Grid templateColumns="repeat(2, minmax(0, 1fr))" gap={2}>
                      {[
                        {
                          label: 'New pages',
                          value: `+${crawlComparison.added_pages.toLocaleString()}`,
                          color: 'status.success.fg',
                        },
                        {
                          label: 'Removed',
                          value: crawlComparison.removed_pages.toLocaleString(),
                          color: 'ui.text.primary',
                        },
                        {
                          label: 'Scores changed',
                          value: crawlComparison.score_changed_pages.toLocaleString(),
                          color: 'ui.text.primary',
                        },
                        {
                          label: 'Average score',
                          value:
                            crawlComparison.average_score_delta == null
                              ? '—'
                              : `${crawlComparison.average_score_delta > 0 ? '+' : ''}${crawlComparison.average_score_delta}`,
                          color:
                            (crawlComparison.average_score_delta || 0) >= 0
                              ? 'status.success.fg'
                              : 'status.warning.fg',
                        },
                      ].map(item => (
                        <Box key={item.label} bg="ui.surface.input" borderRadius="10px" p={3}>
                          <Text fontSize="xs" color="ui.text.secondary">
                            {item.label}
                          </Text>
                          <Text mt={0.5} fontSize="lg" fontWeight="700" color={item.color}>
                            {item.value}
                          </Text>
                        </Box>
                      ))}
                    </Grid>
                    <HStack gap={3} mt={2} color="ui.text.secondary">
                      <Text fontSize="xs">
                        ↑ {crawlComparison.improved_pages.toLocaleString()} improved
                      </Text>
                      <Text fontSize="xs">
                        ↓ {crawlComparison.declined_pages.toLocaleString()} declined
                      </Text>
                    </HStack>
                    <VStack align="stretch" gap={3.5} mt={4}>
                      <CrawlScoreChangeList
                        label="Top improved pages"
                        rows={crawlComparison.top_improved_pages}
                        improved
                      />
                      <CrawlScoreChangeList
                        label="Top worsened pages"
                        rows={crawlComparison.top_worsened_pages}
                        improved={false}
                      />
                    </VStack>
                  </Box>
                ) : selectedCrawl?.status === 'pending' || selectedCrawl?.status === 'running' ? (
                  <Box bg="ui.surface.input" borderRadius="10px" p={3}>
                    <Text fontSize="sm" color="ui.text.primary" fontWeight="600">
                      Crawl in progress
                    </Text>
                    <Text mt={1} fontSize="xs" color="ui.text.secondary">
                      Changes appear after this crawl completes.
                    </Text>
                  </Box>
                ) : selectedCrawl?.status === 'failed' ? (
                  <Box bg="status.warning.bg" borderRadius="10px" p={3}>
                    <Text fontSize="sm" color="status.warning.fg" fontWeight="600">
                      Crawl failed
                    </Text>
                    <Text mt={1} fontSize="xs" color="ui.text.secondary">
                      {selectedCrawl.error || 'No comparison is available for this crawl.'}
                    </Text>
                  </Box>
                ) : selectedCrawl ? (
                  <Box bg="ui.surface.input" borderRadius="10px" p={3}>
                    <Text fontSize="sm" color="ui.text.primary" fontWeight="600">
                      No previous completed crawl
                    </Text>
                    <Text mt={1} fontSize="xs" color="ui.text.secondary">
                      Changes appear after the next crawl.
                    </Text>
                  </Box>
                ) : (
                  <Text fontSize="sm" color="ui.text.secondary">
                    No crawl snapshot is available yet.
                  </Text>
                )}
              </VStack>
            </Box>
          </Grid>
        )}

        {activeWebsiteTab !== 'map' && (
          <WebsiteAnalyticsViews
            projectId={projectId}
            dateRange={websiteDateRange}
            selectedPath={pathFilter || ''}
          />
        )}
      </VStack>
    </Box>
  );
}
